import { apiGet, apiPatch, apiPost, apiDelete } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';
import type { InheritedLink } from '../../../shared/ui/LinkCell.js';

export type DocumentType = 'contract' | 'invoice' | 'act';
export type DocumentStatus = 'draft' | 'sent' | 'signed' | 'cancelled';

export interface DocumentContact {
  id: string;
  firstName: string;
  lastName?: string | null;
  company?: string | null;
}

export interface DocumentDeal {
  id: string;
  title: string;
  amount: number | string;
}

export interface DocumentCompany {
  id: string;
  name: string;
}

export interface DocumentListItem {
  id: string;
  workspaceId: string;
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  amount: number | string | null;
  currency: string;
  contactId: string | null;
  dealId: string | null;
  companyId: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: DocumentContact | null;
  deal?: DocumentDeal | null;
  company?: DocumentCompany | null;
  /**
   * Привязки, подтянутые от соседа: клиент и фирма документа могут стоять у его
   * сделки. Считает сервер общим правилом наследования — грид только показывает.
   */
  inheritedLinks?: Record<string, InheritedLink> | undefined;
}

export interface DocumentDetail extends DocumentListItem {
  body: string;
}

export interface ListDocumentsFilters {
  type?: DocumentType;
  status?: DocumentStatus;
  contactId?: string;
  dealId?: string;
  /** Документы компании: её собственные плюс документы её сделок и контактов. */
  companyId?: string;
}

export interface CreateDocumentInput {
  title: string;
  type: DocumentType;
  contactId?: string;
  dealId?: string;
  companyId?: string;
  amount?: number;
}

export interface UpdateDocumentInput {
  title?: string;
  type?: DocumentType;
  status?: DocumentStatus;
  contactId?: string | null;
  dealId?: string | null;
  companyId?: string | null;
  amount?: number | null;
  body?: string;
}

export function getDocumentsQueryKey(
  workspaceId: string,
  filters: ListDocumentsFilters = {},
): EntityListKey {
  return entityListKey('documents', workspaceId, { ...filters });
}

export function getDocumentDetailQueryKey(
  workspaceId: string,
  documentId: string,
): readonly string[] {
  return entityDetailKey('documents', workspaceId, documentId);
}

export async function listDocuments(
  filters: ListDocumentsFilters = {},
): Promise<DocumentListItem[]> {
  const search = new URLSearchParams();
  if (filters.type) search.set('type', filters.type);
  if (filters.status) search.set('status', filters.status);
  if (filters.contactId) search.set('contactId', filters.contactId);
  if (filters.dealId) search.set('dealId', filters.dealId);
  if (filters.companyId) search.set('companyId', filters.companyId);

  const path = search.size > 0 ? `/documents?${search.toString()}` : '/documents';
  return apiGet<DocumentListItem[]>(path);
}

export async function createDocument(input: CreateDocumentInput): Promise<DocumentListItem> {
  return apiPost<DocumentListItem, CreateDocumentInput>('/documents', input);
}

export async function updateDocument(
  documentId: string,
  input: UpdateDocumentInput,
): Promise<DocumentDetail> {
  return apiPatch<DocumentDetail, UpdateDocumentInput>(`/documents/${documentId}`, input);
}

export async function deleteDocument(documentId: string): Promise<void> {
  return apiDelete(`/documents/${documentId}`);
}

export async function getDocumentById(documentId: string): Promise<DocumentDetail> {
  return apiGet<DocumentDetail>(`/documents/${documentId}`);
}
